// Pure DTO → store-record conversions (SyncMapping.swift). Server strings that mean "unset"
// arrive as "" (Mattermost's convention); nullable columns get a real NULL at this boundary so an
// empty string never leaks into the store as if it were data.
import type { CategoryRecord, ChannelMemberRecord, ChannelRecord, PostFileRecord, PostRecord, ReactionRecord, TeamRecord, UserRecord } from '../../core/records';
import { PostState } from '../../core/records';
import type { RawCategory, RawChannel, RawChannelMember, RawFileInfo, RawPost, RawTeam, RawUser } from '../net/networkClient';

export function nonEmpty(value: string | undefined | null): string | null {
  return value ? value : null;
}

export function teamRecord(t: RawTeam): TeamRecord {
  return {
    id: t.id,
    displayName: t.display_name ?? '',
    name: t.name ?? '',
    description: nonEmpty(t.description),
    updateAt: 0,
    lastTeamIconUpdate: t.last_team_icon_update ?? 0,
  };
}

export function channelRecord(c: RawChannel): ChannelRecord {
  return {
    id: c.id,
    teamId: c.team_id ?? '',
    type: c.type ?? '',
    displayName: c.display_name ?? '',
    name: c.name ?? '',
    header: nonEmpty(c.header),
    purpose: nonEmpty(c.purpose),
    totalMsgCount: c.total_msg_count ?? 0,
    lastPostAt: c.last_post_at ?? 0,
    updateAt: c.update_at ?? 0,
    deleteAt: c.delete_at ?? 0,
    totalMsgCountRoot: c.total_msg_count_root ?? c.total_msg_count ?? 0,
  };
}

/** The RAW `props.customStatus` JSON, kept raw: expiry is judged when the row is DRAWN. */
function customStatusJSON(u: RawUser): string {
  const raw = u.props?.customStatus;
  if (raw == null) return '';
  return typeof raw === 'string' ? raw : JSON.stringify(raw);
}

/**
 * A `props` string Satori Core writes (shared/API.md §37: `satori_area`, `satori_area_id`). Absent,
 * empty or non-text = null. A number id is read as its decimal string (Core sends strings; lenient).
 */
function propString(u: RawUser, key: string): string | null {
  const raw = u.props?.[key];
  if (typeof raw === 'string') return raw.trim() === '' ? null : raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return null;
}

/**
 * Bot accounts seen in any user answer this session (the store has no `is_bot` column). Read by
 * people pickers that must not offer a bot (the call invite, shared/API.md §30.3).
 */
export const botUserIds = new Set<string>();

export function userRecord(u: RawUser): UserRecord {
  if (u.is_bot) botUserIds.add(u.id);
  else if (u.is_bot === false) botUserIds.delete(u.id);
  return {
    id: u.id,
    username: u.username ?? '',
    nickname: nonEmpty(u.nickname),
    firstName: nonEmpty(u.first_name),
    lastName: nonEmpty(u.last_name),
    updateAt: 0,
    email: u.email ?? '',
    lastPictureUpdate: u.last_picture_update ?? 0,
    deleteAt: u.delete_at ?? 0,
    // Carried through EVERY write path: `users` is a full-row upsert, so a mapping that dropped
    // the status would blank it on the next sync of that person.
    customStatusJSON: customStatusJSON(u),
    // Full-row upsert too: a user object without the props clears the área (it was removed in Core).
    satoriArea: propString(u, 'satori_area'),
    satoriAreaId: propString(u, 'satori_area_id'),
    // Mattermost omits `is_bot` for people: absent = a person (full-row upsert).
    isBot: u.is_bot === true,
  };
}

export function channelMemberRecord(m: RawChannelMember): ChannelMemberRecord {
  return {
    channelId: m.channel_id,
    userId: m.user_id,
    msgCount: m.msg_count ?? 0,
    mentionCount: m.mention_count ?? 0,
    lastViewedAt: m.last_viewed_at ?? 0,
    markUnread: m.notify_props?.mark_unread ?? null,
    msgCountRoot: m.msg_count_root ?? m.msg_count ?? 0,
    mentionCountRoot: m.mention_count_root ?? m.mention_count ?? 0,
    notifyPush: m.notify_props?.push ?? null,
    serverUpdateAt: m.last_update_at ?? 0,
  };
}

export function categoryEntries(teamId: string, response: { order?: string[]; categories?: RawCategory[] }): Array<{ category: CategoryRecord; channelIds: string[] }> {
  const byId = new Map((response.categories ?? []).map((c) => [c.id, c]));
  const out: Array<{ category: CategoryRecord; channelIds: string[] }> = [];
  (response.order ?? []).forEach((id, index) => {
    const c = byId.get(id);
    if (!c) return;
    out.push({
      category: {
        id: c.id,
        teamId: c.team_id || teamId,
        type: c.type ?? 'custom',
        displayName: c.display_name ?? '',
        sorting: c.sorting ?? '',
        sortOrder: index,
        collapsed: !!c.collapsed,
        muted: !!c.muted,
      },
      channelIds: c.channel_ids ?? [],
    });
  });
  return out;
}

/** Injected by the sync engine once the PostEmbed port is wired (shared/API.md §12.4). */
export type EmbedReducer = (metadata: RawPost['metadata']) => string | null;

export function postRecord(p: RawPost, state: PostState, embed: EmbedReducer = () => null): PostRecord {
  return {
    id: p.id,
    channelId: p.channel_id,
    userId: p.user_id,
    createAt: p.create_at ?? 0,
    updateAt: p.update_at ?? 0,
    deleteAt: p.delete_at ?? 0,
    message: p.message ?? '',
    rootId: nonEmpty(p.root_id),
    pendingId: p.pending_post_id ? p.pending_post_id : null,
    state,
    type: p.type ?? '',
    props: p.props && Object.keys(p.props).length ? JSON.stringify(p.props) : null,
    isPinned: !!p.is_pinned,
    editAt: p.edit_at ?? 0,
    embedJson: embed(p.metadata),
    priority: p.metadata?.priority?.priority ?? '',
  };
}

export function postFileRecords(p: RawPost): PostFileRecord[] | null {
  const files = p.metadata?.files;
  if (!files) return null; // ABSENT ≠ empty (§12.0): never conclude "no attachments"
  return files.map((f: RawFileInfo, index) => ({
    fileId: f.id,
    postId: p.id,
    channelId: p.channel_id,
    name: f.name ?? '',
    size: f.size ?? 0,
    mimeType: f.mime_type ?? '',
    width: f.width ?? 0,
    height: f.height ?? 0,
    miniPreview: f.mini_preview ?? null,
    sortOrder: index,
  }));
}

export function reactionRecords(p: RawPost): ReactionRecord[] | null {
  const reactions = p.metadata?.reactions;
  if (!p.metadata || reactions === undefined) return null; // ABSENT ≠ empty (§14.0)
  return (reactions ?? []).map((r) => ({ postId: r.post_id || p.id, userId: r.user_id, emojiName: r.emoji_name, createAt: r.create_at ?? 0 }));
}

/** The other participant of "<a>__<b>", excluding the reader; null on the self-DM. */
export function dmOtherParticipantId(channelName: string, currentUserId: string): string | null {
  const parts = channelName.split('__');
  if (parts.length !== 2) return null;
  const other = parts[0] === currentUserId ? parts[1] : parts[0];
  return other === currentUserId ? null : other;
}

/** The DM row's FACE — the other participant, or the reader in their own notes space. */
export function dmPartnerId(channelName: string, currentUserId: string): string | null {
  const parts = channelName.split('__');
  if (parts.length !== 2) return null;
  return parts[0] === currentUserId ? parts[1] : parts[0];
}

/** Ordered posts of a page (`order` is the only thing that determines display order). */
export function orderedPosts(page: { order?: string[]; posts?: Record<string, RawPost> }): RawPost[] {
  const posts = page.posts ?? {};
  return (page.order ?? []).map((id) => posts[id]).filter((p): p is RawPost => !!p);
}

/** SyncPruning.staleTeamIds — an empty server answer prunes nothing. */
export function staleTeamIds(localIds: string[], serverIds: string[]): string[] {
  if (!serverIds.length) return [];
  const keep = new Set(serverIds);
  return localIds.filter((id) => !keep.has(id));
}

/** SyncPruning.stalePostIds — only inside the page's own window, never an unsent row. */
export function stalePostIds(
  local: Array<{ id: string; createAt: number; isAwaitingServer: boolean }>,
  serverIds: string[],
  windowStartCreateAt: number,
): string[] {
  if (!serverIds.length) return [];
  const keep = new Set(serverIds);
  return local.filter((p) => !p.isAwaitingServer && p.createAt >= windowStartCreateAt && !keep.has(p.id)).map((p) => p.id);
}

/**
 * HistoryReach.hasOlderBeyond: an EMPTY `prev_post_id` or a page that did not fill is the server
 * saying "nothing older"; an exactly-full page proves nothing.
 */
export function hasOlderBeyond(pageCount: number, requestedPerPage: number, prevPostId: string | undefined): boolean {
  if (prevPostId === '') return false;
  if (pageCount < requestedPerPage) return false;
  return true;
}

export { PostState };

/**
 * A roots-only page's thread counts (`collapsedThreads=true`, shared/API.md §5.1) for the reply
 * footer — the replies themselves are not on the page. Only ROOTS that carry `reply_count` count;
 * `participants` may be id strings or user objects (extended form): both give the id.
 */
export function rootReplyStats(posts: RawPost[]): Array<{ rootId: string; replyCount: number; lastReplyAt: number; participantIds: string[] }> {
  const out: Array<{ rootId: string; replyCount: number; lastReplyAt: number; participantIds: string[] }> = [];
  for (const p of posts) {
    if (p.root_id || typeof p.reply_count !== 'number') continue;
    const ids = (p.participants ?? [])
      .map((x) => (typeof x === 'string' ? x : x && typeof x.id === 'string' ? x.id : ''))
      .filter(Boolean);
    out.push({ rootId: p.id, replyCount: p.reply_count, lastReplyAt: p.last_reply_at ?? 0, participantIds: [...new Set(ids)] });
  }
  return out;
}
